<script setup>
import {computed} from "vue";
import IssueLabels from "@/components/viewer/IssueLabels.vue";

// Rows are one line tall; longer bodies are cut so every row keeps the same height.
const SNIPPET_LENGTH = 120;

// eslint-disable-next-line no-undef
const props = defineProps({
  comments  : {type: Array, default: () => []},
  modelValue: {type: String, default: null},
  loading   : Boolean,
  hasProject: Boolean
});

// eslint-disable-next-line no-undef
const emit = defineEmits(['update:modelValue']);

const isEmpty = computed(() => !props.loading && props.comments.length === 0);

// The row shows plain text: getStringifiedText() returns markup meant for v-html,
// which cannot be truncated without risking a broken tag.
const getSnippet = (comment) => {
  const text    = (comment.text ?? []).filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
  const snippet = text || (comment.attachments ?? []).map((attachment) => attachment.name).join(', ');
  return snippet.length > SNIPPET_LENGTH ? `${snippet.slice(0, SNIPPET_LENGTH)}…` : snippet;
};
</script>

<template>
  <div v-if="!props.hasProject" class="comment-inbox-message text-medium-emphasis">
    {{ $t('noProjectSelected') }}
  </div>

  <div v-else-if="props.loading" class="comment-inbox-message">
    <v-progress-circular color="secondary" indeterminate size="24"></v-progress-circular>
    <span class="text-medium-emphasis">{{ $t('comments.loading') }}</span>
  </div>

  <div v-else-if="isEmpty" class="comment-inbox-message text-medium-emphasis">
    {{ $t('comments.empty') }}
  </div>

  <v-list v-else class="comment-inbox-list" density="compact">
    <v-list-item v-for="comment in props.comments"
                 :key="comment.id"
                 class="comment-inbox-item"
                 :active="comment.id === props.modelValue"
                 color="secondary"
                 @click="emit('update:modelValue', comment.id)"
    >
      <div class="comment-inbox-row">
        <v-avatar :color="comment.getStatusColor()" size="40">
          <span class="text-subtitle-1">{{ comment.getAuthorAcronym() }}</span>
        </v-avatar>
        <div class="comment-inbox-content">
          <div class="comment-inbox-header">
            <span class="text-subtitle-2 font-weight-bold comment-inbox-author">
              {{ comment.author }}
            </span>
            <span class="text-caption text-medium-emphasis comment-inbox-date">
              {{ comment.getFormattedCommentCreatedDate() }}
            </span>
          </div>
          <div v-if="comment.title" class="text-subtitle-2 comment-inbox-title">
            {{ comment.title }}
          </div>
          <div class="text-body-2 text-medium-emphasis comment-inbox-snippet">
            {{ getSnippet(comment) }}
          </div>
          <div class="comment-inbox-meta">
            <v-chip class="comment-inbox-status-chip"
                    size="x-small"
                    variant="tonal"
                    :color="comment.getStatusColor()"
                    :prepend-icon="comment.getStatusIcon()"
            >
              {{ comment.getStatusLabel() }}
            </v-chip>
            <issue-labels :comment="comment"></issue-labels>
            <span v-if="comment.getReplyCount() > 0" class="text-caption text-medium-emphasis">
              <v-icon icon="mdi-reply" size="x-small"></v-icon>
              {{ comment.getReplyCount() }} {{ $t('comments.replyCount', comment.getReplyCount()) }}
            </span>
          </div>
        </div>
      </div>
    </v-list-item>
  </v-list>
</template>

<style scoped>
@import '@/assets/styles/components/CommentInboxList.css';
</style>
